import { Package } from '@features/DynamicPackagesConfigurationWizard/types';
import { ParsedJourneyParams } from '@store/yourJourney/selectors';
import { ConfigurationGroupType, TimeSlotsType } from 'shared-types/SpaPackage';
import { buildDatesToCheck } from './buildDatesToCheck';
import { getGroupAvailability, isSpaPackageUnavailable } from './isSpaPackageUnavailable';

export type ConfigGroupAvailability = {
  dates: string[];
  availableDates: string[];
  availableDatesByOption: Map<number, string[]>;
};

type PackageExperience = Package['experiences'][number];

export const buildConfigGroupAvailabilityMap = (
  configGroupResults: Array<{ date: string; configGroups: ConfigurationGroupType[] | undefined }>,
  numberOfGuests: number
): Map<number, ConfigGroupAvailability> => {
  const configGroupAvailabilityMap = new Map<number, ConfigGroupAvailability>();

  configGroupResults.forEach(({ date, configGroups }) => {
    if (!configGroups) return;

    configGroups.forEach((group) => {
      if (!configGroupAvailabilityMap.has(group.id)) {
        configGroupAvailabilityMap.set(group.id, {
          dates: [],
          availableDates: [],
          availableDatesByOption: new Map(),
        });
      }

      const groupData = configGroupAvailabilityMap.get(group.id)!;
      groupData.dates.push(date);

      const isAvailable = !isSpaPackageUnavailable([group], numberOfGuests);
      if (isAvailable) {
        groupData.availableDates.push(date);
      }

      (Array.isArray(group.options) ? group.options : []).forEach((option) => {
        if (getGroupAvailability(option) < numberOfGuests) return;

        const optionDates = groupData.availableDatesByOption.get(option.id) ?? [];
        groupData.availableDatesByOption.set(option.id, [...optionDates, date]);
      });
    });
  });

  return configGroupAvailabilityMap;
};

const getExperienceOptionIds = (experience: PackageExperience): number[] => [
  ...new Set(
    (Array.isArray(experience.options) ? experience.options : [])
      .filter((option) => option.itemType !== 'spa-extras')
      .map((option) => Number(option.meta.entityId))
  ),
];

const getRequiredOptions = (experience: PackageExperience, optionIds: number[]): number =>
  experience.numberOfSelectionsRequired > 0
    ? experience.numberOfSelectionsRequired
    : optionIds.length;

const countOptionsAvailableOn = (
  optionIds: number[],
  groupAvailability: ConfigGroupAvailability,
  dates: string[]
): number =>
  optionIds.filter((optionId) =>
    dates.some((date) => groupAvailability.availableDatesByOption.get(optionId)?.includes(date))
  ).length;

const getExperienceAvailableDates = (
  experience: PackageExperience,
  groupAvailability: ConfigGroupAvailability
): string[] => {
  const optionIds = getExperienceOptionIds(experience);

  if (!optionIds.length) return groupAvailability.availableDates;

  const requiredOptions = getRequiredOptions(experience, optionIds);

  return groupAvailability.dates.filter(
    (date) => countOptionsAvailableOn(optionIds, groupAvailability, [date]) >= requiredOptions
  );
};

const isExperienceAvailableWithin = (
  experience: PackageExperience,
  groupAvailability: ConfigGroupAvailability,
  experienceDates: string[]
): boolean => {
  const optionIds = getExperienceOptionIds(experience);

  if (!optionIds.length) {
    return experienceDates.some((date) => groupAvailability.availableDates.includes(date));
  }

  return (
    countOptionsAvailableOn(optionIds, groupAvailability, experienceDates) >=
    getRequiredOptions(experience, optionIds)
  );
};

export const validateSpaPackageConfigGroupAvailability = (
  packageData: Package,
  configGroupAvailabilityMap: Map<number, ConfigGroupAvailability>,
  journeyParams: ParsedJourneyParams,
  isPackageRepeating: boolean
): boolean => {
  if (!packageData.experiences || packageData.experiences.length === 0) {
    return true;
  }

  if (isPackageRepeating) {
    if (!journeyParams.toDate) return false;

    //stayDuration includes checkout day
    const stayDuration = journeyParams.toDate.diff(journeyParams.fromDate, 'days') + 1;
    const minimumRequiredDays = Math.max(1, stayDuration - 1);

    const allExperienceAvailableDates: string[][] = [];

    for (const experience of packageData.experiences) {
      const configGroupId = Number(experience.meta.entityId);
      const groupAvailability = configGroupAvailabilityMap.get(configGroupId);

      if (!groupAvailability) return false;

      const experienceDates = buildDatesToCheck(
        journeyParams,
        experience.dayNumber,
        experience.dayNumberRange,
        isPackageRepeating
      );

      const experienceAvailableDates = getExperienceAvailableDates(experience, groupAvailability);
      const availableDatesForExperience = experienceDates.filter((date) =>
        experienceAvailableDates.includes(date)
      );

      allExperienceAvailableDates.push(availableDatesForExperience);
    }

    if (allExperienceAvailableDates.length === 0) {
      return false;
    }

    const intersection = allExperienceAvailableDates.reduce((acc, dates) => {
      return acc.filter((date) => dates.includes(date));
    }, allExperienceAvailableDates[0]);

    return intersection.length >= minimumRequiredDays;
  }

  return packageData.experiences.every((experience) => {
    const configGroupId = Number(experience.meta.entityId);
    const groupAvailability = configGroupAvailabilityMap.get(configGroupId);

    if (!groupAvailability) return false;

    const experienceDates = buildDatesToCheck(
      journeyParams,
      experience.dayNumber,
      experience.dayNumberRange,
      isPackageRepeating
    );

    return isExperienceAvailableWithin(experience, groupAvailability, experienceDates);
  });
};

export const validateSpaPackageTimeBandAvailability = (
  isPackageRepeating: boolean,
  journeyParams: ParsedJourneyParams,
  timeBandDates: TimeSlotsType
): boolean => {
  if (isPackageRepeating) {
    if (!journeyParams.toDate) return false;
    //stayDuration includes checkout day
    const stayDuration = journeyParams.toDate.diff(journeyParams.fromDate, 'days') + 1;
    const minimumRequiredDays = Math.max(1, stayDuration - 1);

    const datesWithAvailability = Object.keys(timeBandDates).reduce<string[]>((dates, date) => {
      const timeslotsOnDay = Object.values(timeBandDates[date]).filter(
        (slot) => slot !== undefined
      );
      if (timeslotsOnDay.length === 0) return dates;
      return dates.concat(date);
    }, []);

    return datesWithAvailability.length >= minimumRequiredDays;
  } else {
    const experienceDates = buildDatesToCheck(journeyParams, 0, undefined, isPackageRepeating)[0];
    return experienceDates in timeBandDates && timeBandDates[experienceDates].length > 0;
  }
};
